import { gql } from "@apollo/client";
import type { SyncableClinicalProductCategory } from "elephant-web/gql";
import { useMemo } from "react";
import type { RxCollection, RxDatabase, WithDeleted } from "rxdb";
import type { ClinicalProductCategory as RxClinicalProductCategory } from "../collections/clinical-product-category";
import type { RxDBCollections } from "../provider";
import {
  SyncClinicalProductCategoryPullDocument,
  type WholeSyncableClinicalProductCategoryFragment,
} from "./__generated__/clinical-product-category.gql";
import { useGqlReplication } from "./use-gql-replication";

export const useReplicateClinicalProductCategory = ({
  db,
  collection,
}: {
  db?: RxDatabase<RxDBCollections>;
  collection?: RxCollection<RxClinicalProductCategory>;
}) => {
  const pull = useMemo(
    () => ({
      query: SyncClinicalProductCategoryPullDocument,
      buildInputVariables: async (checkpoint, batchSize) => ({
        input: {
          limit: batchSize,
          checkpoint,
        },
      }),
      buildPullCheckpoint: async (doc) => ({
        id: doc.id,
        updatedAt: doc.meta.updatedAt,
      }),
      reSyncInterval: { hours: 1 },
    }),
    [],
  );

  return useGqlReplication<
    RxClinicalProductCategory,
    SyncableClinicalProductCategory,
    null
  >({
    db,
    collection,
    pull,
    mapGqlToRx: mapGqlClinicalProductCategoryToRxClinicalProductCategory,
    batchSize: 100,
    metadataLocalStorageKeySuffix: "clinical-product-category",
  });
};

const mapGqlClinicalProductCategoryToRxClinicalProductCategory = (
  clinicalProductCategory: WholeSyncableClinicalProductCategoryFragment,
): WithDeleted<RxClinicalProductCategory> => ({
  id: clinicalProductCategory.id,
  title: {
    en: clinicalProductCategory.title.en,
  },
  meta: {
    createdAt: clinicalProductCategory.meta.createdAt,
    createdByUserId: clinicalProductCategory.meta.createdByUserId,
    updatedAt: clinicalProductCategory.meta.updatedAt,
    updatedByUserId: clinicalProductCategory.meta.updatedByUserId,
  },
  _deleted: clinicalProductCategory.meta.deleted,
});

gql`
  fragment WholeSyncableClinicalProductCategory on SyncableClinicalProductCategory {
    id
    title {
      en
    }
    meta {
      createdAt
      createdByUserId
      updatedAt
      updatedByUserId
      deleted
    }
  }
`;

gql`
  query SyncClinicalProductCategoryPull($input: PullClinicalProductCategoryInput!) {
    pullClinicalProductCategory(input: $input) {
      checkpoint {
        id
        updatedAt
      }
      documents {
        ...WholeSyncableClinicalProductCategory
      }
    }
  }
`;
